import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LocationService } from '../location.service';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-location-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterLink],
    templateUrl: './location-form.component.html',
    styleUrl: './location-form.component.scss',
})
export class LocationFormComponent implements OnInit {
    locationForm: FormGroup;
    isEditMode = false;
    isLoading = false;
    locationId: string | null = null;
    parentLocations: any[] = [];
    // Must match the backend LocationsTypeEnum — 'Clinic'/'Other' were listed
    // here before but the API rejects them with a 400. 'DamageArea' is the
    // physical damage bay inside a warehouse: pick the warehouse as its
    // parent so its stock sits on its own row, off the sellable one.
    locationTypes = ['Warehouse', 'Store', 'DamageArea'];
    readonly typeLabels: Record<string, string> = {
        Warehouse: 'Warehouse',
        Store: 'Store',
        DamageArea: 'Damage Area (inside a warehouse)',
    };

    private fb = inject(FormBuilder);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private locationService = inject(LocationService);
    private toastr = inject(ToastrService);

    constructor() {
        this.locationForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(3)]],
            type: ['', Validators.required],
            parentLocationId: [null],
            isActive: [true],
        });
    }

    ngOnInit(): void {
        this.locationId = this.route.snapshot.paramMap.get('id');
        this.isEditMode = !!this.locationId;

        this.loadParentLocations();

        if (this.isEditMode && this.locationId) {
            this.loadLocationData(this.locationId);
        }
    }

    loadParentLocations(): void {
        this.locationService.getLocations(true).subscribe({
            next: (res: any) => {
                this.parentLocations = (res?.data || res || []).filter((loc: any) => loc.id !== this.locationId);
            },
            error: (err) => console.error('Error loading parent locations:', err),
        });
    }

    loadLocationData(id: string): void {
        this.isLoading = true;
        this.locationService.getLocationById(id).subscribe({
            next: (res: any) => {
                const location = res?.data || res;
                this.locationForm.patchValue({
                    name: location.name,
                    type: location.type,
                    parentLocationId: location.parentLocationId,
                    isActive: location.isActive,
                });
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error loading location data:', err);
                this.toastr.error('Failed to load location details.');
                this.isLoading = false;
            },
        });
    }

    onSubmit(): void {
        if (this.locationForm.invalid) {
            this.toastr.warning('Please fill all required fields correctly.');
            return;
        }

        this.isLoading = true;
        const formData = this.locationForm.value;

        if (this.isEditMode && this.locationId) {
            this.locationService.updateLocation(this.locationId, formData).subscribe({
                next: () => {
                    this.toastr.success('Location updated successfully!');
                    this.router.navigate(['/locations']);
                },
                error: (err) => {
                    console.error('Error updating location:', err);
                    this.toastr.error('Failed to update location.');
                    this.isLoading = false;
                },
            });
        } else {
            this.locationService.createLocation(formData).subscribe({
                next: () => {
                    this.toastr.success('Location created successfully!');
                    this.router.navigate(['/locations']);
                },
                error: (err) => {
                    console.error('Error creating location:', err);
                    this.toastr.error('Failed to create location.');
                    this.isLoading = false;
                },
            });
        }
    }
}
